import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { LookupUser } from 'wre-authlib';

@Component({
  selector: 'wtw-user-lookup',
  templateUrl: './user-lookup.component.html',
  styleUrl: './user-lookup.component.sass',
  standalone: false
})
export class WTWUserLookupComponent {
  @Input() isMultiSelect: boolean = true;
  @Input() displayBadge: boolean = true;
  @Input() control = new FormControl();
  @Input() inputId = 'userLookupId';
  @Input() placeholderValue = 'Search user...';
  @Input() isDisabled = false;
  @Input() isInvalid?: boolean;
  @Input() labelText?: string;
  @Input() inputTabIndex?: string;
  @Input() inputName?: string;
  @Input() isHorizontal: boolean = false;
  @Input() loading: boolean = true;
  @Input() list: LookupUser[] = [];
  @Input() readOnly = false;
  @Input() emptyListText = 'The list Empty/ReadOnly';
  @Input() noSearchResultText = 'No user found';

  @Output() searchEvent = new EventEmitter<string>();

  @ViewChild(NgbDropdown) dropdown?: NgbDropdown;

  @HostListener('keydown', ['$event']) onKeydownHandler(event: KeyboardEvent) {
    if (event.code === 'Tab' && this.dropdown?.isOpen()) {
      this.dropdown.close();
    }
  }

  get name(): string {
    return this.inputName ?? this.inputId;
  }
  get inputValue(): string {
    return this.isMultiSelect
      ? this.searchTerm
      : this.control.value
        ? this.control.value.displayName
        : '';
  }

  dropdownKey = '';
  searchTerm = '';
  private subject: Subject<null> = new Subject();

  existingUsers: LookupUser[] = [];

  isNewUser(user: LookupUser): boolean {
    return !this.existingUsers.includes(user);
  }

  ngOnInit() {
    this.existingUsers = this.isMultiSelect && this.control.value ? [...this.control.value] : [];
    this.dropdownKey = `${this.inputId}-userList`;
    this.subject.pipe(debounceTime(200)).subscribe(() => {
      this.searchUsers();
    });
  }

  // search for users
  searchUsers() {
    const searchTerm = this.searchTerm?.trim();
    if (searchTerm?.length > 2) {
      this.searchEvent.emit(searchTerm);
      this.dropdown?.open();
    }
  }

  // Add to user lookup list
  selectUser(lookupUser: LookupUser) {
    if (this.isMultiSelect) {
      const currentValue: LookupUser[] = this.control.value ?? [];

      if (currentValue.filter((v) => v.id === lookupUser.id).length === 0) {
        currentValue.push(lookupUser);
        this.control.setValue(currentValue);
        this.control.markAsDirty();
      }

      this.searchTerm = '';
      document.getElementById(this.inputId)?.focus();
    } else {
      this.control.setValue(lookupUser);
      this.control.markAsDirty();
    }

    this.dropdown?.close();
  }

  // remove from user lookup list
  removeUser(user: LookupUser) {
    if (user !== undefined) {
      const currentControlValue = this.control.value as LookupUser[];
      this.control.setValue(currentControlValue.filter((v) => v.id !== user.id));
      this.control.markAsDirty();
    }
  }

  onInput(event: Event) {
    this.searchTerm = (event.target as HTMLInputElement).value;
  }

  // shared accessibility functions
  onBlur() {
    if (this.isMultiSelect) {
      if (this.searchTerm.length > 2 && this.dropdown?.isOpen() && this.list.length === 0) {
        this.dropdown.close();
      }
      this.searchTerm = '';
      this.control.markAllAsTouched();
    }
  }

  onKeyUp(event: KeyboardEvent) {
    switch (event.key) {
      case 'Escape': {
        this.dropdown?.close();
        break;
      }
      case 'ArrowDown': {
        this.selectItemInDropdownList(event.key);
        break;
      }
      case 'ArrowUp': {
        this.selectItemInDropdownList(event.key);
        break;
      }
      case 'Enter': {
        this.selectItemInDropdownList(event.key);
        break;
      }
      default: {
        this.subject.next(null);
        this.dropdown?.close();
        break;
      }
    }
  }

  selectItemInDropdownList(eventKey: string) {
    if (!this.loading) {
      const list = document.getElementById(this.dropdownKey);
      const selected = list?.querySelector('.selected');
      const element = document.getElementById(this.inputId);
      const scrollHeight = element === null ? 0 : element.getBoundingClientRect().height;
      if (selected) {
        switch (eventKey) {
          case 'ArrowUp': {
            const prevElement = selected.previousElementSibling;
            if (prevElement) {
              selected.classList.remove('selected');
              prevElement.classList.add('selected');
              list?.scrollTo({ top: (list.scrollTop -= scrollHeight), behavior: 'smooth' });
            }
            break;
          }
          case 'ArrowDown': {
            const nextElement = selected.nextElementSibling;
            if (nextElement) {
              selected.classList.remove('selected');
              nextElement.classList.add('selected');
              list?.scrollTo({ top: (list.scrollTop += scrollHeight), behavior: 'smooth' });
            }
            break;
          }
          case 'Enter': {
            (selected as HTMLElement).click();
          }
        }
      } else {
        const subjectToSelect = list?.querySelector('a');
        if (subjectToSelect) {
          subjectToSelect.classList.add('selected');
        }
      }
    }
  }

  // single user select accessibility
  onFocus() {
    if (this.isMultiSelect) {
      return;
    }

    this.searchTerm = this.inputValue;
  }
}
